import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import {
  useCreateTournament,
  useListAdminTournaments,
} from '../../../api/generated/endpoints/admin/admin'
import type { AdminTournamentRead } from '../../../api/generated/model'
import { apiMessage } from '../../../lib/api-error'
import { formatDayLong, todayAtFacility } from '../../../lib/time'
import { TOURNAMENT_STATUS_LABELS } from '../../../lib/tournaments'

/**
 * Every tournament, drafts included, and a form to start a new one.
 *
 * Same rules as the rest of the facility screens: big targets, one column, no
 * menus. The coach does this once a month, which means he will have forgotten
 * how it works each time — so nothing here is discoverable only by hovering.
 */
export const Route = createFileRoute('/_authed/_admin/admin/tournaments')({
  component: AdminTournamentsPage,
})

function AdminTournamentsPage() {
  const [creating, setCreating] = useState(false)
  const tournaments = useListAdminTournaments()

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-semibold">Torneos</h1>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Nuevo torneo
          </button>
        )}
      </div>

      {creating && <CreateForm onDone={() => setCreating(false)} />}

      {tournaments.isPending && <p className="text-slate-600">Cargando…</p>}
      {tournaments.isError && (
        <p role="alert" className="text-red-700">
          No se pudieron cargar los torneos.
        </p>
      )}

      {tournaments.data && tournaments.data.tournaments.length === 0 && !creating && (
        <p className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">
          Todavía no hay torneos. Al crear uno queda como borrador, así que nadie lo ve hasta que se
          abran las inscripciones.
        </p>
      )}

      <ul className="space-y-4">
        {(tournaments.data?.tournaments ?? []).map((tournament) => (
          <TournamentRow key={tournament.id} tournament={tournament} />
        ))}
      </ul>
    </div>
  )
}

function TournamentRow({ tournament }: { tournament: AdminTournamentRead }) {
  const scored = tournament.entries.filter((entry) => entry.gross_strokes !== null).length
  const owing = tournament.entries.filter((entry) => !entry.paid).length

  return (
    <li className="rounded-lg border-2 border-slate-300 bg-white">
      <Link
        to="/admin/tournament/$tournamentId"
        params={{ tournamentId: tournament.id }}
        className="block px-6 py-5 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <span className="text-2xl font-semibold">{tournament.name}</span>
          <StatusPill status={tournament.status} />
        </div>
        <p className="mt-1 text-slate-700">
          {formatDayLong(tournament.event_date)}
          {tournament.gspro_course && <> &middot; {tournament.gspro_course}</>}
        </p>
        <p className="mt-2 text-base text-slate-600">
          {tournament.entrants}
          {tournament.max_players !== null && <> de {tournament.max_players}</>}{' '}
          {/* Agrees with the FIRST number, not the cap: "1 de 16 inscripto"
              is right and "1 de 16 inscriptos" is not. */}
          {tournament.entrants === 1 ? 'inscripto' : 'inscriptos'} &middot; {scored} con tarjeta
          {owing > 0 && <span className="ml-2 font-medium text-amber-700">{owing} sin pagar</span>}
        </p>
      </Link>
    </li>
  )
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'open'
      ? 'bg-green-100 text-green-900'
      : status === 'draft'
        ? 'bg-slate-200 text-slate-700'
        : status === 'closed'
          ? 'bg-amber-100 text-amber-900'
          : 'bg-slate-900 text-white'
  return (
    <span className={`rounded-full px-4 py-1 text-base font-medium ${tone}`}>
      {TOURNAMENT_STATUS_LABELS[status] ?? status}
    </span>
  )
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [eventDate, setEventDate] = useState(todayAtFacility())
  const [course, setCourse] = useState('')
  const [fee, setFee] = useState('')
  const [maxPlayers, setMaxPlayers] = useState('')
  const [playsFrom, setPlaysFrom] = useState('')
  const [playsUntil, setPlaysUntil] = useState('')
  const [roundMinutes, setRoundMinutes] = useState('')

  const create = useCreateTournament({
    mutation: {
      retry: false,
      onSuccess: (tournament) =>
        navigate({
          to: '/admin/tournament/$tournamentId',
          params: { tournamentId: tournament.id },
        }),
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate({
          data: {
            name: name.trim(),
            event_date: eventDate,
            gspro_course: course.trim() || null,
            // Left blank means "the house price" — the backend reads it from
            // the products table rather than the form inventing a number.
            entry_fee_uyu_pesos: fee.trim() === '' ? null : Number(fee),
            max_players: maxPlayers.trim() === '' ? null : Number(maxPlayers),
            notes: null,
            // Blank means "the same day", which is what a tournament was
            // before rounds could be played across a period.
            plays_from: playsFrom || null,
            plays_until: playsUntil || null,
            // Blank takes the facility setting, the same way the fee does.
            round_minutes: roundMinutes.trim() === '' ? null : Number(roundMinutes),
          },
        })
      }}
      className="space-y-5 rounded-lg border-2 border-slate-300 bg-white p-6"
    >
      <h2 className="text-2xl font-semibold">Nuevo torneo</h2>

      <Field label="Nombre">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={200}
          placeholder="Torneo de Agosto"
          className={INPUT}
        />
      </Field>

      <Field label="Fecha">
        <input
          type="date"
          value={eventDate}
          onChange={(event) => setEventDate(event.target.value)}
          required
          className={INPUT}
        />
      </Field>

      <Field label="Cancha de GSPro" hint="Opcional. Se escribe a mano: GSPro no lo informa.">
        <input
          value={course}
          onChange={(event) => setCourse(event.target.value)}
          maxLength={200}
          placeholder="Pebble Beach"
          className={INPUT}
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Costo de inscripción" hint="En blanco toma el precio habitual.">
          <input
            type="number"
            min={0}
            value={fee}
            onChange={(event) => setFee(event.target.value)}
            placeholder="500"
            className={INPUT}
          />
        </Field>
        <Field label="Máximo de jugadores" hint="En blanco, sin límite.">
          <input
            type="number"
            min={1}
            value={maxPlayers}
            onChange={(event) => setMaxPlayers(event.target.value)}
            className={INPUT}
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Se puede jugar desde" hint="En blanco, sólo el día del torneo.">
          <input
            type="date"
            value={playsFrom}
            onChange={(event) => setPlaysFrom(event.target.value)}
            className={INPUT}
          />
        </Field>
        <Field label="Hasta" hint="En blanco, sólo el día del torneo.">
          <input
            type="date"
            value={playsUntil}
            onChange={(event) => setPlaysUntil(event.target.value)}
            className={INPUT}
          />
        </Field>
      </div>

      <Field
        label="Duración de una ronda"
        hint="En minutos. En blanco toma la duración habitual de los ajustes. 120 para 18 hoyos, 60 para nueve."
      >
        <input
          type="number"
          min={10}
          step={10}
          value={roundMinutes}
          onChange={(event) => setRoundMinutes(event.target.value)}
          placeholder="120"
          className={INPUT}
        />
      </Field>

      {/* This used to say "remember to block the bays as well", which is now
          the opposite of the advice: blocking them stops entrants booking. */}
      <p className="rounded-md bg-slate-100 p-4 text-base text-slate-700">
        Empieza como <strong>borrador</strong>: nadie lo ve ni se puede inscribir hasta abrir las
        inscripciones. Cada inscripto reserva su propio horario, así que conviene dejar los
        simuladores libres esos días en lugar de bloquearlos.
      </p>

      {create.isError && (
        <p role="alert" className="text-red-700">
          {apiMessage(create.error, 'No se pudo crear el torneo.')}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={create.isPending}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {create.isPending ? 'Creando…' : 'Crear'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-16 rounded-md border-2 border-slate-400 px-8 text-xl"
        >
          Cancelar
        </button>
      </div>
    </form>
  )
}

export const INPUT =
  'min-h-14 w-full rounded-md border-2 border-slate-400 px-4 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900'

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-base font-medium text-slate-700">{label}</span>
      {hint && <span className="ml-2 text-sm text-slate-500">{hint}</span>}
      <span className="mt-1 block">{children}</span>
    </label>
  )
}
